//Angular
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ResourceLoadState } from '@app/models/resource-load.state';
import { CoreModule } from '@app/modules/core/core.module';
import { languageFallbackMap } from '@app/services/i18n/language-fallback-map';
import { Translations } from '@app/services/i18n/translations.service';
// 3rd Party dependencies
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import {
  PXInitService,
  ZonarOwnerIdInterceptor,
  ZonarUiAuthModule
} from '@zonar-ui/auth';
import { Options } from '@zonar-ui/auth/lib/options/options';
//Zonar Pattern Library
import { ZonarUICoreModule } from '@zonar-ui/core';
import { FeatureTogglesModule } from '@zonar-ui/feature-toggle';
import { ZonarUiFooterModule } from '@zonar-ui/footer';
import {
  I18nService,
  TranslateService,
  ZonarUiI18nModule
} from '@zonar-ui/i18n';
import { ZonarUiProgressSpinnerModule } from '@zonar-ui/progress-spinner';
import { SidenavModule } from '@zonar-ui/sidenav';
import { ZonarUiMapModule } from '@zonar-ui/zonar-ui-map';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
// App-level Imports
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { startupFactory } from './startup.factory';

const ZonarInterceptorOptions: Options = {
  zonarOwnerIdInterceptor: {
    requiresZonarOwnerId: [
      environment.apiBase.url,
      environment.userPreferencesApiBase.url,
      environment.locationApi.assetPropertiesUrl
    ]
  }
};

export function loadAppTranslations(
  translateService: TranslateService,
  i18nService: I18nService,
  translations: Translations
): () => Observable<any> {
  return () => {
    return translateService
      .use(
        i18nService.getLanguageInUse(
          environment.i18n.supportedLanguages,
          environment.i18n.defaultLanguage,
          languageFallbackMap,
          true
        )
      )
      .pipe(
        tap(() => {
          translations.translationsLoadState.next(
            ResourceLoadState.LOAD_SUCCESSFUL
          );
        }),
        switchMap((lang) => of(lang))
      );
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    AuthModule.forRoot({
      audience: environment.auth.audience,
      clientId: environment.auth.clientID,
      domain: environment.auth.domain,
      redirect_uri: window.location.origin,
      scope: 'openid profile email',
      max_age: 36000,
      useRefreshTokens: environment.auth.useRefreshTokens,
      cacheLocation: 'localstorage',
      httpInterceptor: {
        allowedList: [
          `${environment.apiBase.url}/*`,
          `${environment.geocoderApi.url}/*`,
          `${environment.locationApi.assetPropertiesUrl}/*`,
          `${environment.coreEntityApiBase.url}/*`,
          `${environment.userPreferencesApiBase.url}/*`,
          /* allowing beta2 is necessary for apis on beta versions (e.g., settings) */
          `${environment.coreEntityApiBase.url}beta2/*`
        ]
      }
    }),
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    FlexLayoutModule,
    HttpClientModule,
    FeatureTogglesModule,
    NgxSkeletonLoaderModule,
    ZonarUiMapModule.forRoot({
      hereApiKey: environment.here.apiKeyV3,
      usingV3HereApi: true
    }),
    SidenavModule.forRoot({ stage: environment.authEnv || null }),
    ZonarUiAuthModule,
    ZonarUICoreModule,
    ZonarUiI18nModule,
    ZonarUiFooterModule,
    ZonarUiProgressSpinnerModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: startupFactory,
      deps: [PXInitService],
      multi: true
    },
    { provide: 'region', useValue: environment.region },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: 'options', useValue: ZonarInterceptorOptions },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ZonarOwnerIdInterceptor,
      multi: true
    },
    { provide: 'applicationId', useValue: environment.auth.applicationId },
    {
      provide: 'supportedLanguages',
      useValue: environment.i18n.supportedLanguages
    },
    { provide: 'defaultLanguage', useValue: environment.i18n.defaultLanguage },
    { provide: 'useBrowserLanguage', useValue: true },
    { provide: 'logoutReturnToUrl', useValue: window.location.origin },
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppTranslations,
      deps: [TranslateService, I18nService, Translations],
      multi: true
    },
    { provide: 'devCycleClientId', useValue: environment.devCycle.clientId }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
